import { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore, useIsAdmin } from '../../auth';
import { APP_VERSION, APP_COPYRIGHT_YEAR } from '../../version';
import { ErrorBoundary } from '../ErrorBoundary';
import {
  LayoutDashboard,
  Users,
  Building2,
  FolderKanban,
  Layers,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
} from 'lucide-react';
import clsx from 'clsx';

interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
  adminOnly?: boolean;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', href: '/', icon: LayoutDashboard },
  { label: 'Clienti', href: '/clients', icon: Building2 },
  { label: 'Progetti', href: '/projects', icon: FolderKanban },
  { label: 'Catalogo', href: '/catalog', icon: Layers },
  { label: 'Utenti', href: '/users', icon: Users, adminOnly: true },
  { label: 'Impostazioni', href: '/settings', icon: Settings, adminOnly: true },
];

export function MainLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const isAdmin = useIsAdmin();
  
  const filteredNavItems = navItems.filter(
    (item) => !item.adminOnly || isAdmin
  );
  
  const handleLogout = async () => {
    await logout();
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar - FIXED on all screens */}
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200',
          'transform transition-transform duration-200 ease-in-out',
          'lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 flex-shrink-0">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">SC</span>
              </div>
              <span className="font-semibold text-gray-900">Config Builder</span>
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-md text-gray-500 hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {filteredNavItems.map((item) => {
              const isActive = location.pathname === item.href ||
                (item.href !== '/' && location.pathname.startsWith(item.href));
              
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={clsx(
                    'flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  )}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="w-5 h-5 mr-3" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
          
          {/* User section */}
          <div className="border-t border-gray-200 p-4 flex-shrink-0">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center w-full px-3 py-2 rounded-md hover:bg-gray-100 transition-colors"
            >
              <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-gray-600 text-sm font-medium">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              <div className="ml-3 flex-1 text-left min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{user?.name || 'Utente'}</p>
                <p className="text-xs text-gray-500">{user?.role}</p>
              </div>
              <ChevronDown className={clsx(
                'w-4 h-4 text-gray-500 transition-transform flex-shrink-0',
                userMenuOpen && 'rotate-180'
              )} />
            </button>
            
            {userMenuOpen && (
              <div className="mt-2 bg-white rounded-md shadow-lg border border-gray-200 py-1">
                <button
                  onClick={handleLogout}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Esci
                </button>
              </div>
            )}
          </div>

          {/* Version footer */}
          <div className="px-4 py-3 border-t border-gray-100 flex-shrink-0">
            <p className="text-xs text-gray-400 text-center">
              v{APP_VERSION} · © {APP_COPYRIGHT_YEAR}
            </p>
          </div>
        </div>
      </aside>
      
      {/* Main content - offset by sidebar width */}
      <div className="lg:ml-64 min-h-screen flex flex-col">
        {/* Top bar (mobile only) */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200 lg:hidden flex-shrink-0">
          <div className="flex items-center justify-between h-16 px-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-md text-gray-500 hover:bg-gray-100"
            >
              <Menu className="w-6 h-6" />
            </button>
            <span className="font-semibold text-gray-900">Config Builder</span>
            <div className="w-10" />
          </div>
        </header>
        
        {/* Page content */}
        <main className="flex-1 p-6 lg:p-8">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
